import { XMarkIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import type { FilterChangedEvent, GridApi } from 'ag-grid-community';
import { useCallback, useRef, useState } from 'react';

export interface UseFilterBannerResult {
  filtersActive: boolean;
  handleFilterChanged: (event: FilterChangedEvent) => void;
  clearFilters: () => void;
  registerGridApi: (api: GridApi) => void;
}

/**
 * Drives the shared "Filters applied / Clear Filters" banner (MCV-1265).
 * Grids that use BasicGrid pick this up automatically; grids that render
 * AgGridReact directly should call this hook and render <FilterBanner> above
 * the grid, wiring registerGridApi into onGridReady and handleFilterChanged
 * into onFilterChanged.
 */
export function useFilterBanner(): UseFilterBannerResult {
  const gridApiRef = useRef<GridApi | null>(null);
  const [filtersActive, setFiltersActive] = useState(false);

  const registerGridApi = useCallback((api: GridApi) => {
    gridApiRef.current = api;
  }, []);

  const handleFilterChanged = useCallback((_event: FilterChangedEvent) => {
    const api = gridApiRef.current;
    if (!api) return;
    const model = api.getFilterModel();
    setFiltersActive(Object.keys(model).length > 0);
  }, []);

  const clearFilters = useCallback(() => {
    gridApiRef.current?.setFilterModel({});
  }, []);

  return { filtersActive, handleFilterChanged, clearFilters, registerGridApi };
}

export const FilterBanner = ({ active, onClear }: { active: boolean; onClear: () => void }) => {
  if (!active) return null;
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-grey-900 bg-primary-t-900 px-3 py-1.5">
      <span className="text-xs text-grey-300">Filters applied</span>
      <button
        type="button"
        data-cy={CT.agGrid.clearFiltersBtn}
        onClick={onClear}
        className="ml-auto flex items-center gap-1 text-xs font-medium text-primary-50 transition-colors hover:text-primary-t-300"
      >
        <XMarkIcon className="size-3.5" aria-hidden="true" />
        Clear Filters
      </button>
    </div>
  );
};
